import {useEffect, useRef} from 'react';

import {AxiosPromise} from 'axios';
import {useDispatch, useSelector} from 'react-redux';

import {ENDPOINTS} from 'constants/endpoints';
import {startCheckoutRootSaga} from 'desktop/pages/checkout/store/sagas';
import {setCmsPopupVisibility} from 'desktop/reducers/cmsPopup';
import {AppThunkDispatchType} from 'desktop/store';
import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {useHostDispatch} from 'hooks/hostHooks/hostHooks';
import {useGetCountries} from 'hooks/location/location';
import {apiConfig, HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';

import {getUrlSearchParam} from 'libs/url/url';

import {getOrderReviewItems} from '../../store/reducers/orderReviewItems/orderReviewItems';
import {startDeliveryStepSaga} from '../../store/reducers/sagaStep/sagaStep';
import {getOrderReviewItemsData} from '../../store/selectors/orderReviewItems/orderReviewItems';

/**
 * Save quote by token
 * @param {string} token token of the saved quote
 * @return {AxiosPromise}
 */
function loadQuoteByToken(token: string): AxiosPromise {
    return apiConfig.instanceGusaBackend({
        data: {
            token,
        },
        headers: {
            [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.APPLICATION_JSON,
        },
        method: 'post',
        url: ENDPOINTS.LOAD_QUOTE_BY_TOKEN,
    });
}

/**
 * initial hook for checkout page
 */
export const useInitialCheckoutHook = () => {
    startCheckoutRootSaga();
    useGetCustomerData();
    useGetCountries();
    const dispatch = useDispatch<AppThunkDispatchType>();
    const dispatchToHost = useHostDispatch();
    const {list, status} = useSelector(getOrderReviewItemsData);
    const hasInitializedCheckoutRef = useRef(false);

    useEffect(() => {
        (async () => {
            dispatch(getOrderReviewItems({collectTax: false, withTaxErrors: false}));
            try {
                const tokenFromUrl: string = getUrlSearchParam('token');
                if (tokenFromUrl) await loadQuoteByToken(tokenFromUrl);
            } catch (error) {
                logWithSentry(error, 'Invalid query parameter token used in cart page');
            }
        })();
    }, []);

    useEffect(() => {
        // Need this to avoid dispatches when optimaxcheckout/v2/cart/items request updated
        if (hasInitializedCheckoutRef.current === true) return;
        if (status === 'FULFILLED' && list.length) {
            dispatchToHost({
                payload: {
                    action: 'index',
                    controller: 'onepage',
                    module: 'checkout',
                },
                type: 'ROUTER@SET_ROUTER_INFO',
            });
            dispatch(startDeliveryStepSaga());
            dispatch(setCmsPopupVisibility({content: null, visibility: false}));
            hasInitializedCheckoutRef.current = true;
        }
    }, [status, list, dispatchToHost, dispatch]);
};
